import { useState } from "react";
import { X } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { trpc } from "@/hooks/trpc";
import { toast } from "sonner";
import type { FoodMeasurementTypeId, TestTemplateId, TestTemplateAttributeId } from "@brewbase/db/types";

export function MeasurementTestLink({
  measurementTypeId,
  linkedAttributeId,
  linkedTemplateName,
  linkedAttributeName,
}: {
  measurementTypeId: FoodMeasurementTypeId;
  linkedAttributeId: TestTemplateAttributeId | null;
  linkedTemplateName: string | null;
  linkedAttributeName: string | null;
}) {
  const [editing, setEditing] = useState(false);
  const [selectedTemplateId, setSelectedTemplateId] = useState<string>("");
  const [selectedAttrId, setSelectedAttrId] = useState<string>("");
  const utils = trpc.useUtils();

  const { data: templates = [] } = trpc.food.testTemplates.list.useQuery(undefined, {
    enabled: editing,
  });

  const templateIdNum = selectedTemplateId ? (Number(selectedTemplateId) as TestTemplateId) : null;

  const { data: attributes = [] } = trpc.food.testTemplates.listAttributes.useQuery(
    templateIdNum!,
    { enabled: editing && templateIdNum != null },
  );

  const setAttributeMutation = trpc.food.measurementTypes.setTestAttribute.useMutation({
    onSuccess: () => {
      utils.food.measurementTypes.list.invalidate();
      setEditing(false);
    },
    onError: (err) => toast.error(err.message),
  });

  function handleStartEdit() {
    setSelectedTemplateId("");
    setSelectedAttrId("");
    setEditing(true);
  }

  function handleCancel() {
    setEditing(false);
    setSelectedTemplateId("");
    setSelectedAttrId("");
  }

  function handleSave() {
    if (!selectedAttrId) return;
    setAttributeMutation.mutate({
      measurement_type_id: measurementTypeId,
      test_template_attribute_id: Number(selectedAttrId) as TestTemplateAttributeId,
    });
  }

  function handleUnlink() {
    setAttributeMutation.mutate({
      measurement_type_id: measurementTypeId,
      test_template_attribute_id: null,
    });
  }

  if (!editing) {
    return (
      <div className="flex items-center gap-1.5 min-w-0">
        <button
          type="button"
          onClick={handleStartEdit}
          className="text-xs text-muted-foreground hover:text-foreground transition-colors truncate text-left"
        >
          {linkedTemplateName
            ? `${linkedTemplateName} → ${linkedAttributeName ?? "?"}`
            : "Link test…"}
        </button>
        {linkedAttributeId && (
          <button
            type="button"
            onClick={handleUnlink}
            className="shrink-0 text-muted-foreground hover:text-destructive transition-colors opacity-0 group-hover:opacity-100"
            title="Remove link"
          >
            <X className="h-3 w-3" />
          </button>
        )}
      </div>
    );
  }

  return (
    <div className="flex items-center gap-1.5 shrink-0">
      <Select
        value={selectedTemplateId}
        onValueChange={(v) => {
          setSelectedTemplateId(v);
          setSelectedAttrId("");
        }}
      >
        <SelectTrigger className="h-7 text-xs w-36">
          <SelectValue placeholder="Template…" />
        </SelectTrigger>
        <SelectContent>
          {templates.map((t) => (
            <SelectItem key={t.id} value={String(t.id)} className="text-xs">
              {t.name}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>

      <Select
        value={selectedAttrId}
        onValueChange={setSelectedAttrId}
        disabled={!selectedTemplateId || attributes.length === 0}
      >
        <SelectTrigger className="h-7 text-xs w-36">
          <SelectValue placeholder="Attribute…" />
        </SelectTrigger>
        <SelectContent>
          {attributes.map((a) => (
            <SelectItem key={a.id} value={String(a.id)} className="text-xs">
              {a.attribute_name}
              {a.attribute_unit ? ` (${a.attribute_unit})` : ""}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>

      <Button
        size="sm"
        variant="outline"
        className="h-7 px-2 text-xs"
        disabled={!selectedAttrId || setAttributeMutation.isPending}
        onClick={handleSave}
      >
        Save
      </Button>

      <Button
        variant="ghost"
        size="icon"
        className="h-7 w-7 text-muted-foreground"
        onClick={handleCancel}
      >
        <X className="h-3.5 w-3.5" />
      </Button>
    </div>
  );
}
